// ---------------------------------------------------------------------------
// FieldBoundaryEditor — a satellite map with mapbox-gl-draw for drawing and
// reshaping farm field boundaries, with the deal's parcels shown as context.
//
// Reused in three places:
//   • the AI detection review step (many editable suggestions),
//   • the Add/Edit Field modal (one boundary — draw new or reshape saved),
//   • hand-drawing a brand-new field from scratch.
//
// Fields are drawn as solid AMBER filled boxes (with a number badge at the
// centroid) so they read clearly as "what's a field" and never blend into the
// CYAN parcel outlines drawn underneath as context.
//
// The map always frames the deal's parcels: it fits to the seed field(s) when
// there are any, otherwise to the parcels — and if the parcel geometry arrives
// after mount (async fetch) it fits to it once it lands.
//
// Uncontrolled after mount: `initialFeatures` seeds the draw canvas once, then
// the map is the source of truth and every change is emitted via
// onFeaturesChange([{ id, geometry }]). Parent state should NOT be fed back into
// `initialFeatures` (that would clobber an in-progress edit).
// ---------------------------------------------------------------------------
import { useEffect, useRef } from 'react';
import { B } from '../../constants/brand';
import { ensureMapboxDrawLoaded, MAPBOX_TOKEN } from './ParcelsMapTab';
import { bboxOfFC } from '../../utils/fieldGeo';

// Field boundaries: solid amber, distinct from the cyan (#22d3ee) parcels.
const FIELD = '#f59e0b';

// Custom mapbox-gl-draw theme. The stock theme colors polygons a faint blue
// (~#3bb2d0 @ 0.1) that is nearly invisible AND almost identical to our cyan
// parcels — the exact reason a user can't tell what the AI detected. This
// paints each field as a solid amber box with a bold outline, keeps the vertex
// / midpoint handles for editing, and stamps a number badge at each field's
// centroid (from the feature's `label` property, exposed as `user_label`).
const FIELD_DRAW_STYLES = [
  // Polygon fill — inactive (not selected).
  {
    id: 'gl-draw-polygon-fill-inactive',
    type: 'fill',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    paint: { 'fill-color': FIELD, 'fill-outline-color': FIELD, 'fill-opacity': 0.3 },
  },
  // Polygon fill — active (selected / being edited).
  {
    id: 'gl-draw-polygon-fill-active',
    type: 'fill',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    paint: { 'fill-color': FIELD, 'fill-outline-color': FIELD, 'fill-opacity': 0.4 },
  },
  // Polygon outline — inactive.
  {
    id: 'gl-draw-polygon-stroke-inactive',
    type: 'line',
    filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': FIELD, 'line-width': 2.5 },
  },
  // Polygon outline — active (dashed to signal it's editable).
  {
    id: 'gl-draw-polygon-stroke-active',
    type: 'line',
    filter: ['all', ['==', 'active', 'true'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': FIELD, 'line-dasharray': [0.3, 2], 'line-width': 2.5 },
  },
  // The line rubber-banded while drawing a new polygon.
  {
    id: 'gl-draw-line-active',
    type: 'line',
    filter: ['all', ['==', '$type', 'LineString'], ['==', 'active', 'true']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': FIELD, 'line-dasharray': [0.3, 2], 'line-width': 2.5 },
  },
  // Number badge at each field's centroid (from `user_label`, when present).
  {
    id: 'gl-draw-field-label',
    type: 'symbol',
    filter: ['all', ['==', '$type', 'Polygon'], ['has', 'user_label']],
    layout: {
      'text-field': ['get', 'user_label'],
      'text-size': 16,
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
      'text-allow-overlap': true,
    },
    paint: { 'text-color': '#fff', 'text-halo-color': '#78350f', 'text-halo-width': 2 },
  },
  // Midpoints (click to add a vertex).
  {
    id: 'gl-draw-polygon-midpoint',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3.5, 'circle-color': FIELD },
  },
  // Vertex halo (white ring) for grip visibility.
  {
    id: 'gl-draw-polygon-and-line-vertex-halo-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 6, 'circle-color': '#fff' },
  },
  // Vertex points (draggable).
  {
    id: 'gl-draw-polygon-and-line-vertex-active',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'vertex'], ['==', '$type', 'Point'], ['!=', 'mode', 'static']],
    paint: { 'circle-radius': 4, 'circle-color': FIELD },
  },
];

export const FieldBoundaryEditor = ({ parcelsFC, initialFeatures = [], onFeaturesChange, height = 360, single = false }) => {
  const nodeRef = useRef(null);
  const mapRef  = useRef(null);
  const drawRef = useRef(null);
  const cbRef   = useRef(onFeaturesChange);
  cbRef.current = onFeaturesChange;
  // Snapshot the seed features / single flag once so re-renders don't reseed.
  const seedRef   = useRef(initialFeatures);
  const singleRef = useRef(single);
  // parcelsFC can arrive after mount (async fetch) — keep a live ref so the
  // map + effects always see the latest, and track whether we've framed it yet.
  const parcelsRef      = useRef(parcelsFC);
  parcelsRef.current    = parcelsFC;
  const loadedRef       = useRef(false);
  const framedParcelRef = useRef(false);

  // Add/refresh the cyan parcel context layer; fit to it the first time it is
  // available when there are no seed fields to frame instead.
  const syncParcels = (map) => {
    const fc = parcelsRef.current;
    if (!map || !fc) return;
    if (map.getSource('fbe-parcels')) {
      map.getSource('fbe-parcels').setData(fc);
    } else {
      map.addSource('fbe-parcels', { type: 'geojson', data: fc });
      map.addLayer({ id: 'fbe-parcels-line', type: 'line', source: 'fbe-parcels',
        paint: { 'line-color': '#22d3ee', 'line-width': 1.5, 'line-opacity': 0.85 } });
    }
    if (!framedParcelRef.current && !seedRef.current.length) {
      const box = bboxOfFC(fc);
      if (box) {
        try {
          map.fitBounds([[box.west, box.south], [box.east, box.north]], { padding: 30, maxZoom: 17, duration: 0 });
          framedParcelRef.current = true;
        } catch { /* ignore fit errors */ }
      }
    }
  };

  useEffect(() => {
    if (!nodeRef.current) return;
    if (!MAPBOX_TOKEN) return;
    let disposed = false;
    let map;

    const emit = () => {
      const draw = drawRef.current;
      if (!draw) return;
      const feats = (draw.getAll().features || []).map(f => ({ id: f.id, geometry: f.geometry }));
      cbRef.current?.(feats);
    };

    (async () => {
      try {
        await ensureMapboxDrawLoaded();
        if (disposed || !nodeRef.current) return;
        window.mapboxgl.accessToken = MAPBOX_TOKEN;

        // Seed each field with a 1-based number badge (skipped in single mode).
        const seedFC = { type: 'FeatureCollection', features: seedRef.current.map((f, i) => ({
          type: 'Feature', id: f.id,
          properties: singleRef.current ? {} : { label: String(i + 1) },
          geometry: f.geometry,
        })) };
        // Frame the seed fields if any, else the deal's parcels.
        const focusBox = bboxOfFC(seedFC) || bboxOfFC(parcelsRef.current);
        if (focusBox) framedParcelRef.current = true; // already framed at creation

        map = new window.mapboxgl.Map({
          container: nodeRef.current,
          style: 'mapbox://styles/mapbox/satellite-streets-v12',
          ...(focusBox
            ? { bounds: [[focusBox.west, focusBox.south], [focusBox.east, focusBox.north]], fitBoundsOptions: { padding: 30, maxZoom: 17 } }
            : { center: [-98.5, 39.5], zoom: 4 }),
        });
        mapRef.current = map;

        map.on('load', () => {
          if (disposed) return;
          loadedRef.current = true;
          // Parcels context (cyan outline, non-editable) — added first so the
          // amber fields draw on top.
          syncParcels(map);
          const draw = new window.MapboxDraw({
            displayControlsDefault: false,
            controls: { polygon: true, trash: true },
            userProperties: true, // exposes `label` as `user_label` for the badge
            styles: FIELD_DRAW_STYLES,
          });
          map.addControl(draw, 'top-right');
          drawRef.current = draw;
          if (seedFC.features.length) draw.add(seedFC);
          setTimeout(() => { try { map.resize(); } catch {} }, 60);

          // In single-boundary mode a newly drawn polygon replaces any prior one.
          map.on('draw.create', () => {
            if (singleRef.current) {
              const all = draw.getAll().features;
              if (all.length > 1) all.slice(0, -1).forEach(f => draw.delete(f.id));
            }
            emit();
          });
          map.on('draw.update', emit);
          map.on('draw.delete', emit);
        });
      } catch { /* editor is best-effort; parent shows a fallback message */ }
    })();

    return () => { disposed = true; try { map && map.remove(); } catch {} mapRef.current = null; drawRef.current = null; loadedRef.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Parcels can load after the map — add the layer / frame them when they land.
  useEffect(() => {
    if (loadedRef.current && mapRef.current) syncParcels(mapRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelsFC]);

  if (!MAPBOX_TOKEN) {
    return <div style={{ padding: 16, fontSize: 13, color: B.textMd, background: B.surfaceMuted, borderRadius: 8 }}>
      Mapbox is not configured (REACT_APP_MAPBOX_TOKEN), so boundaries can't be drawn on a map.
    </div>;
  }
  return <div ref={nodeRef} style={{ width: '100%', height, borderRadius: 8, overflow: 'hidden', background: B.border }} />;
};
